import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { axios, API_URL, useSelector, useDispatch, toast } from '../Data/Imports';
import { FiChevronLeft, FiChevronRight } from '../Data/Icons';
import Tables from '../UI/Tables';
import { getStockLabel } from '../Redux/features/label/labelSlice';
import ProductFilters from '../common/ProductFilters';

const ProductList = () => {
	const { stockLabels, isLoading } = useSelector((state) => state.label);
	const [products, setProducts] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const dispatch = useDispatch();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const pageFromUrl = queryParams.get('page');
	const initialPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
	const [filters, setFilters] = useState({});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchProducts(initialPage, filters);
	}, [initialPage, filters]);

	const handleFilterChange = (updatedFilters) => {
		setFilters(updatedFilters);
		setCurrentPage(1);
		fetchProducts(1, updatedFilters);
	};

	const handleReset = () => {
		setFilters({});
		setCurrentPage(1);
		fetchProducts(1, {});
	};

	const fetchProducts = async (page, appliedFilters) => {
		setLoading(true);
		try {
			const response = await axios.post(
				`${API_URL}/stock/get/get-all-stock?page=${page}&limit=10`,
				appliedFilters
			);
			setProducts(response?.data?.Stock?.data);
			setTotalPages(response?.data?.Stock?.totalPages);
		} catch {
			setProducts([]);
			setTotalPages(1);
		}
		setLoading(false);
	};

	const [hasFetched, setHasFetched] = useState(false);
	useEffect(() => {
		if (stockLabels?.length === 0 && !isLoading && !hasFetched) {
			dispatch(getStockLabel());
			setHasFetched(true);
		}
	}, [stockLabels?.length, isLoading, dispatch, hasFetched]);

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			const newPage = currentPage + 1;
			setCurrentPage(newPage);
			fetchProducts(newPage, filters);
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			const newPage = currentPage - 1;
			setCurrentPage(newPage);
			fetchProducts(newPage, filters);
		}
	};

	const toggleIsDisplayPortal = async (data) => {
		try {
			const response = await axios.post(`${API_URL}/stock/update/toggle-stock-isdisplay`, data);
			toast.success(response?.data?.message);
			fetchProducts(currentPage, filters);
		} catch (error) {
			toast.error(error?.response?.data?.message);
		}
	};

	return (
		<div className='p-6 shadow-lg rounded-lg bg-gray-100 w-3/4'>
			<h1 className='text-2xl font-bold mb-4 text-center'>Product List</h1>
			{/* Filters */}
			<ProductFilters onFilterChange={handleFilterChange} handleReset={handleReset} />
			{/* Render the table */}
			<Tables
				headings={stockLabels}
				data={products}
				loading={loading}
				toggleIsDisplayPortal={toggleIsDisplayPortal}
				fetchProducts={() => fetchProducts(currentPage, filters)}
			/>

			{/* Pagination controls */}
			{totalPages > 1 && (
				<div className='mt-6 flex justify-center items-center gap-4'>
					<button
						onClick={handlePrevPage}
						disabled={currentPage === 1}
						className={`w-10 h-10 flex items-center justify-center rounded-full ${
							currentPage === 1
								? 'bg-gray-300 text-gray-500'
								: 'bg-[#3649F9] text-white hover:bg-blue-700'
						}`}>
						<FiChevronLeft />
					</button>
					<div className='text-gray-700'>
						Page {currentPage} of {totalPages}
					</div>
					<button
						onClick={handleNextPage}
						disabled={currentPage === totalPages}
						className={`w-10 h-10 flex items-center justify-center rounded-full ${
							currentPage === totalPages
								? 'bg-gray-300 text-gray-500'
								: 'bg-[#3649F9] text-white hover:bg-blue-700'
						}`}>
						<FiChevronRight />
					</button>
				</div>
			)}
		</div>
	);
};

export default ProductList;
